import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, CheckCircle, AlertCircle, Play, MapPin, Calendar, Clock, Trash2, ZoomIn, Package, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { agentDashboardService } from '../../../services/agentDashboardService';

/**
 * Delivery Mode Modal Component
 * Handles delivery workflow: Démarrer → EN_LIVRAISON, Problème, Incident, Terminer → LIVRE
 * Features: Photo upload (max 5), signature canvas, photo gallery with zoom, incident reporting
 * Locks all inputs when status is LIVRE or INCIDENT
 */
export default function DeliveryModeModal({ task, onClose, onUpdate }) {
  const [status, setStatus] = useState(task?.status);
  const [photos, setPhotos] = useState(task?.deliveryPhotoUrls || []);
  const [signature, setSignature] = useState(task?.deliverySignatureUrl || null);
  const [notes, setNotes] = useState(task?.deliveryNotes || '');
  
  // Incident reporting state
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentType, setIncidentType] = useState(task?.incidentType || '');
  const [incidentComment, setIncidentComment] = useState(task?.incidentComment || '');
  
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [zoomedPhoto, setZoomedPhoto] = useState(null);
  
  // Canvas refs
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const isLocked = status === 'LIVRE' || status === 'INCIDENT';
  const MAX_PHOTOS = 5;

  // Incident types
  const INCIDENT_TYPES = [
    { value: 'CLIENT_ABSENT', label: 'Client absent' },
    { value: 'REFUS_CLIENT', label: 'Refus du client' },
    { value: 'ADRESSE_INCORRECTE', label: 'Adresse incorrecte' },
    { value: 'ACCES_IMPOSSIBLE', label: 'Accès impossible' },
    { value: 'COLIS_ENDOMMAGE', label: 'Colis endommagé' },
    { value: 'AUTRE', label: 'Autre incident' }
  ];

  useEffect(() => {
    if (canvasRef?.current && !signature) {
      setupCanvas();
    }
  }, [canvasRef]);

  const setupCanvas = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    if (isLocked) return;
    setIsDrawing(true);
    setHasSignature(true);
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    const rect = canvas?.getBoundingClientRect();
    const x = e?.clientX ? e?.clientX - rect?.left : e?.touches?.[0]?.clientX - rect?.left;
    const y = e?.clientY ? e?.clientY - rect?.top : e?.touches?.[0]?.clientY - rect?.top;
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || isLocked) return;
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    const rect = canvas?.getBoundingClientRect();
    const x = e?.clientX ? e?.clientX - rect?.left : e?.touches?.[0]?.clientX - rect?.left;
    const y = e?.clientY ? e?.clientY - rect?.top : e?.touches?.[0]?.clientY - rect?.top;
    ctx?.lineTo(x, y);
    ctx?.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (isLocked) return;
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    setSignature(null);
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    if (isLocked || photos?.length >= MAX_PHOTOS) return;

    const files = Array.from(e?.target?.files);
    if (files?.length === 0) return;

    // Check if adding these files would exceed the limit
    if (photos?.length + files?.length > MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos autorisées`);
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadedUrls = [];

      for (const file of files) {
        // Validate file type
        if (!file?.type?.startsWith('image/')) {
          throw new Error('Seules les images sont autorisées');
        }

        // Validate file size (5MB max)
        if (file?.size > 5 * 1024 * 1024) {
          throw new Error('Taille maximale: 5MB par photo');
        }

        // Upload to Supabase storage
        const fileName = `delivery-photos/${task?.id}/${Date.now()}-${file?.name}`;
        const { data, error: uploadError } = await supabase?.storage?.from('shipment-photos')?.upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL (shipment-photos is a public bucket)
        const { data: urlData } = supabase?.storage?.from('shipment-photos')?.getPublicUrl(data?.path);

        uploadedUrls?.push(urlData?.publicUrl);
      }

      setPhotos(prev => [...prev, ...uploadedUrls]);
      setSuccess('Photos ajoutées avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError(err?.message || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  // Remove photo handler
  const handleRemovePhoto = (index) => {
    if (isLocked) return;
    setPhotos(prev => prev?.filter((_, i) => i !== index));
  };

  // Upload signature to storage
  const uploadSignature = async () => {
    if (!hasSignature || isLocked) return null;

    try {
      const canvas = canvasRef?.current;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      const fileName = `delivery-signatures/${task?.id}/${Date.now()}-signature.png`;
      const { data, error: uploadError } = await supabase?.storage?.from('signatures')?.upload(fileName, blob, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get signed URL (signatures is a private bucket)
      const { data: urlData, error: urlError } = await supabase?.storage?.from('signatures')?.createSignedUrl(data?.path, 31536000); // 1 year expiry

      if (urlError) throw urlError;

      return urlData?.signedUrl;
    } catch (err) {
      console.error('Error uploading signature:', err);
      throw err;
    }
  };

  // Workflow button handlers
  const handleStart = async () => {
    if (status !== 'A_PREPARER') return;

    setSaving(true);
    setError('');

    try {
      await agentDashboardService?.updateDeliveryTaskStatus(task?.id, {
        status: 'EN_LIVRAISON'
      });

      setStatus('EN_LIVRAISON');
      setSuccess('Livraison démarrée');
      setTimeout(() => setSuccess(''), 3000);
      onUpdate?.();
    } catch (err) {
      console.error('Error starting delivery:', err);
      setError('Erreur lors du démarrage');
    } finally {
      setSaving(false);
    }
  };

  const handleProblem = async () => {
    if (status === 'LIVRE' || status === 'INCIDENT') return;

    if (!notes?.trim()) {
      setError('Commentaire obligatoire pour signaler un problème');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await agentDashboardService?.updateDeliveryTaskStatus(task?.id, {
        status: 'PROBLEME',
        deliveryNotes: notes
      });

      setStatus('PROBLEME');
      setSuccess('Problème signalé');
      setTimeout(() => setSuccess(''), 3000);
      onUpdate?.();
    } catch (err) {
      console.error('Error reporting problem:', err);
      setError('Erreur lors du signalement');
    } finally {
      setSaving(false);
    }
  };

  // NEW: Handle incident reporting
  const handleReportIncident = () => {
    setShowIncidentModal(true);
  };

  const handleIncidentSubmit = async () => {
    if (!incidentType) {
      setError('Type d\'incident obligatoire');
      return;
    }

    if (!incidentComment?.trim()) {
      setError('Commentaire d\'incident obligatoire');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await agentDashboardService?.updateDeliveryTaskStatus(task?.id, {
        status: 'INCIDENT',
        incidentType: incidentType,
        incidentComment: incidentComment
      });

      setStatus('INCIDENT');
      setShowIncidentModal(false);
      setSuccess('Incident signalé - Livraison bloquée');
      setTimeout(() => {
        setSuccess('');
        onUpdate?.();
      }, 2000);
    } catch (err) {
      console.error('Error reporting incident:', err);
      setError(err?.message || 'Erreur lors du signalement de l\'incident');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (status === 'LIVRE' || status === 'INCIDENT') return;

    setSaving(true);
    setError('');

    try {
      // Upload signature if present
      let signatureUrl = signature;
      if (hasSignature && !signature) {
        signatureUrl = await uploadSignature();
      }

      // Update delivery task
      await agentDashboardService?.updateDeliveryTaskStatus(task?.id, {
        status: 'LIVRE',
        deliveryPhotoUrls: photos,
        deliverySignatureUrl: signatureUrl,
        deliveryNotes: notes
      });

      setStatus('LIVRE');
      setSignature(signatureUrl);
      setSuccess('Livraison terminée');
      setTimeout(() => {
        setSuccess('');
        onUpdate?.();
        onClose?.();
      }, 2000);
    } catch (err) {
      console.error('Error completing delivery:', err);
      setError(err?.message || 'Erreur lors de la finalisation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 rounded-lg p-2">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Mode Livraison</h2>
                <p className="text-sm text-gray-600">{task?.recipientName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Status Badges */}
            <div className="flex items-center justify-between">
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  status === 'A_PREPARER' ? 'bg-gray-100 text-gray-800' :
                  status === 'EN_LIVRAISON' ? 'bg-blue-100 text-blue-800' :
                  status === 'LIVRE' ? 'bg-green-100 text-green-800' :
                  status === 'INCIDENT'? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                }`}>
                  {status === 'A_PREPARER' ? 'À préparer' :
                   status === 'EN_LIVRAISON' ? 'En livraison' :
                   status === 'LIVRE' ? 'Livré' :
                   status === 'INCIDENT' ? 'Incident signalé' : 'Problème'}
                </span>
              </div>
              {isLocked && (
                <span className="text-sm text-gray-500 flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Verrouillé</span>
                </span>
              )}
            </div>

            {/* Alert Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            {/* Incident Display (if status is INCIDENT) */}
            {status === 'INCIDENT' && task?.incidentType && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-orange-900 mb-2">Incident signalé</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-orange-700 font-medium">Type:</p>
                        <p className="text-sm text-orange-900">
                          {INCIDENT_TYPES?.find(t => t?.value === task?.incidentType)?.label || task?.incidentType}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-orange-700 font-medium">Commentaire:</p>
                        <p className="text-sm text-orange-900">{task?.incidentComment}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Info */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start space-x-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-900">{task?.deliveryAddress}</p>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-600">
                    {new Date(task?.plannedDate)?.toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-600">{task?.plannedTimeWindow}</p>
                </div>
              </div>
              <div className="text-sm">
                <p className="text-gray-600">Contact: {task?.recipientPhone}</p>
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commentaires {status === 'PROBLEME' && <span className="text-red-600">*</span>}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                disabled={isLocked}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Ajouter des notes sur la livraison..."
              />
            </div>

            {/* Photos Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photos de livraison ({photos?.length}/{MAX_PHOTOS})
              </label>
              
              {/* Photo Upload Button */}
              {!isLocked && photos?.length < MAX_PHOTOS && (
                <label className="flex items-center justify-center space-x-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors cursor-pointer">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Téléchargement...' : 'Ajouter des photos'}
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={uploading || isLocked}
                    className="hidden"
                  />
                </label>
              )}

              {/* Photo Gallery */}
              {photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {photos?.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Photo ${index + 1} de livraison du colis pour ${task?.recipientName}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity rounded-lg flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                          <button
                            onClick={() => setZoomedPhoto(url)}
                            className="p-2 bg-white rounded-lg hover:bg-gray-100"
                          >
                            <ZoomIn className="w-4 h-4 text-gray-700" />
                          </button>
                          {!isLocked && (
                            <button
                              onClick={() => handleRemovePhoto(index)}
                              className="p-2 bg-white rounded-lg hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Signature Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature client
              </label>
              
              {signature ? (
                <div className="border border-gray-300 rounded-lg p-2">
                  <img
                    src={signature}
                    alt="Signature du client lors de la livraison"
                    className="w-full h-32 object-contain"
                  />
                </div>
              ) : (
                <>
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className={`w-full border-2 border-gray-300 rounded-lg ${
                      isLocked ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-crosshair'
                    }`}
                  />
                  {!isLocked && (
                    <button
                      onClick={clearSignature}
                      disabled={!hasSignature}
                      className="mt-2 text-sm text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Effacer la signature
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Workflow Buttons */}
            {!isLocked && (
              <div className="flex flex-col space-y-2 pt-4">
                {status === 'A_PREPARER' && (
                  <button
                    onClick={handleStart}
                    disabled={saving}
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Play className="w-5 h-5" />
                    <span>{saving ? 'Démarrage...' : 'Démarrer la livraison'}</span>
                  </button>
                )}

                {(status === 'EN_LIVRAISON' || status === 'PROBLEME') && (
                  <>
                    <button
                      onClick={handleReportIncident}
                      disabled={saving}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <AlertTriangle className="w-5 h-5" />
                      <span>Signaler un incident</span>
                    </button>

                    <button
                      onClick={handleProblem}
                      disabled={saving || !notes?.trim()}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <AlertCircle className="w-5 h-5" />
                      <span>{saving ? 'Signalement...' : 'Signaler un problème'}</span>
                    </button>

                    <button
                      onClick={handleComplete}
                      disabled={saving}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-5 h-5" />
                      <span>{saving ? 'Finalisation...' : 'Terminer la livraison'}</span>
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Delivered Info */}
            {task?.deliveredAt && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                Livré le {new Date(task.deliveredAt)?.toLocaleString('fr-FR')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NEW: Incident Reporting Modal */}
      {showIncidentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-orange-100 rounded-lg p-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Signaler un incident</h3>
              </div>
              <button
                onClick={() => setShowIncidentModal(false)}
                disabled={saving}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Incident Type Select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type d'incident <span className="text-red-600">*</span>
                </label>
                <select
                  value={incidentType}
                  onChange={(e) => setIncidentType(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un type</option>
                  {INCIDENT_TYPES?.map((type) => (
                    <option key={type?.value} value={type?.value}>
                      {type?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Incident Comment Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={incidentComment}
                  onChange={(e) => setIncidentComment(e?.target?.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Décrivez l'incident en détail..."
                />
              </div>

              {/* Warning Message */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>Attention:</strong> Une fois l'incident signalé, la tâche de livraison sera bloquée et aucune modification ne sera possible.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowIncidentModal(false)}
                  disabled={saving}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>
                <button
                  onClick={handleIncidentSubmit}
                  disabled={saving || !incidentType || !incidentComment?.trim()}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Signalement...' : 'Confirmer l\'incident'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Zoom Modal */}
      {zoomedPhoto && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setZoomedPhoto(null)}
        >
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setZoomedPhoto(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
            <img
              src={zoomedPhoto}
              alt="Photo agrandie de la livraison"
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
}